"use client";

import React from "react";
import { Clock, MapPin, Phone, ShieldCheck, UserCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: Clock,
    label: "Same-Day Service"
  }, {
    icon: UserCheck,
    label: "Walk-Ins Welcome"
  }, {
    icon: ShieldCheck,
    label: "Free Visual Inspection"
  }];
  return <section id="hero" className="relative overflow-hidden bg-gradient-to-b from-[#FAF8F5] via-[#F4F6F9] to-[#EAEFF5] py-9 text-[#071B3A] sm:py-11 md:py-16">
      {/* Background Soft Decorative Ambient Glows */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 left-1/4 size-[600px] rounded-lg bg-[#0B4DBA]/10 blur-3xl" />
        <div className="absolute -bottom-24 right-0 size-[450px] rounded-lg bg-[#FF5A00]/10 blur-3xl" />
      </div>

      <div className="mx-auto max-w-[1180px] px-4 sm:px-6 lg:px-8">
        {/* Two-Column Grid: 56% Text / 44% Image starting at md (900px equivalent) */}
        <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-[56%_44%] md:gap-10 xl:gap-14">
          {/* Left Column: Location, Text, CTAs */}
          <div className="flex w-full min-w-0 flex-col items-start text-left">
            {/* Top Location Badge */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: -12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="mb-4 inline-flex items-center gap-2 rounded-lg border border-[#0B4DBA]/20 bg-white/90 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-[#071B3A] shadow-xs backdrop-blur-md">
              <MapPin className="size-3.5 text-[#FF5A00]" />
              <span>Pico Blvd &bull; Los Angeles, CA</span>
            </motion.div>

            {/* Primary Headline with responsive fluid clamp */}
            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1,
            ease: "easeOut"
          }} style={{
            fontSize: "clamp(1.75rem, 4vw + 1rem, 3.25rem)"
          }} className="font-default font-extrabold uppercase italic leading-[1.08] tracking-tight text-[#071B3A]">
              Need New Tires in Los Angeles?
            </motion.h1>

            {/* Supporting Copy */}
            <motion.p initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.2,
            ease: "easeOut"
          }} className="mt-4 max-w-2xl text-base font-medium leading-relaxed text-[#071B3A]/85 sm:text-lg">
              Get the right tires for your vehicle with same-day installation and balancing at Nova Tire Center on Pico Blvd. Walk-ins welcome.
            </motion.p>

            {/* CTA Buttons Container */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.3,
            ease: "easeOut"
          }} className="mt-7 flex w-full min-w-0 flex-col gap-3.5 sm:flex-row sm:items-center sm:gap-4">
              {/* Primary Orange Call Action */}
              <Button asChild size="lg" className="h-auto w-full min-w-0 py-3.5 px-6 bg-[#FF5A00] text-base font-bold text-white shadow-md transition-[transform,background-color,box-shadow] hover:scale-105 hover:bg-[#E55100] active:scale-95 sm:w-auto">
                <a href="tel:+14243138474" className="flex items-center justify-center gap-2">
                  <Phone className="size-5 shrink-0" />
                  <span className="whitespace-nowrap">Call Now</span>
                </a>
              </Button>

              {/* Secondary Outline Directions Action */}
              <Button asChild size="lg" variant="outline" className="h-auto w-full min-w-0 border-2 border-[#0B4DBA] bg-white/80 py-3.5 px-6 text-base font-bold text-[#0B4DBA] shadow-xs backdrop-blur-sm transition-[transform,background-color,color] hover:bg-[#0B4DBA]/10 hover:text-[#0B4DBA] active:scale-95 sm:w-auto">
                <Link to="https://www.google.com/maps/dir/?api=1&destination=8635+W+Pico+Blvd+Los+Angeles+CA+90035" newTab className="flex items-center justify-center gap-2">
                  <MapPin className="size-5 shrink-0 text-[#FF5A00]" />
                  <span className="whitespace-nowrap">Get Directions</span>
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Column: Hero Image Panel */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }} className="w-full min-w-0 overflow-hidden rounded-2xl border border-[#0B4DBA]/15 bg-white p-2 shadow-lg md:rounded-3xl md:p-3">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650379/img/exec-26ba7713-7998-4e12-a024-2df55acdec04.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650379/img/exec-26ba7713-7998-4e12-a024-2df55acdec04.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1650379/img/exec-26ba7713-7998-4e12-a024-2df55acdec04.png-wvc-sizes" alt="New tires in a clean tire service shop." className="aspect-[4/3] max-h-[220px] w-full max-w-full min-w-0 rounded-xl object-cover sm:max-h-[300px] md:max-h-[360px] md:rounded-2xl" loading="eager" />
          </motion.div>
        </div>

        {/* Full-Width Trust Badges Row (Below Text/Image Grid) */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-10 w-full min-w-0 border-t border-[#071B3A]/10 pt-6 sm:mt-12 sm:pt-8">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 lg:gap-6">
            {trustHighlights.map((item, index) => {
            const IconComponent = item.icon;
            return <div key={index} data-index={index} className="flex items-center gap-3 rounded-lg border border-[#0B4DBA]/15 bg-white/90 px-4 py-3 text-sm font-semibold text-[#071B3A] shadow-xs backdrop-blur-sm">
                  <IconComponent className="size-5 shrink-0 text-[#FF5A00]" />
                  <span className="min-w-0 truncate sm:whitespace-normal">{item.label}</span>
                </div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}